// The five analytics views: their ids, their names, and the shape of their URLs.
//
// ITS OWN MODULE BECAUSE THREE PLACES NEED IT AND NONE OF THEM SHOULD IMPORT THE
// OTHERS. The sidebar needs the list to build the ANALYTICS group; the Insights
// route needs it for the tab strip and to validate the URL; the Home cards need
// `insightsPath` so a "Search Console" card does not link at the Bots view. When
// this lived inside routes/Insights.tsx the sidebar duplicated the names, which
// lasted exactly as long as it took to notice they had already drifted — the tab
// strip said "Search" and the nav said "Search Console" from two literals.
//
// A sixth view is one entry here plus one icon in Layout, and nothing can go out
// of step because there is nothing to keep in step.

export const INSIGHTS_TABS = [
  // `label` is the tab strip, where the surrounding page already says these are
  // analytics; `nav` is the sidebar, where the name has to say WHOSE numbers
  // these are — "Search" in a list beside Bots and GA4 does not.
  { id: "search", label: "Search", nav: "Search Console" },
  { id: "bots", label: "Crawlers & bots", nav: "Bots" },
  { id: "audience", label: "Google Analytics", nav: "Google Analytics" },
  { id: "assistants", label: "Assistants", nav: "Assistants" },
  { id: "audit", label: "Audit", nav: "Audit" },
] as const;

/**
 * AI Visibility is its own page (/ai-visibility), not an Insights tab: the sidebar
 * links it directly and every /insights/visibility link redirects there (App.tsx).
 */
export const AI_VISIBILITY_PATH = "/ai-visibility";

export type InsightsTab = (typeof INSIGHTS_TABS)[number]["id"];

/**
 * Bots, because it is the only one of the five that needs nothing connected to
 * show a number — the worker logs every hit whether or not Google has ever been
 * authorised. Landing a new account on an empty Search Console would say the
 * product is broken rather than that it is not set up yet.
 */
export const DEFAULT_INSIGHTS_TAB: InsightsTab = "bots";

export function isInsightsTab(v: string | undefined): v is InsightsTab {
  return !!v && INSIGHTS_TABS.some((t) => t.id === v);
}

/**
 * One place that knows the shape of these URLs, so every link agrees.
 *
 * Audience always carries `view=ga4`: a bare /insights/audience is the old
 * visitors link, and insightsRedirect (features/analytics-home/redirects.tsx)
 * sends it to Bloxx's own Traffic tab instead of Google Analytics. A bare
 * /insights/<id> is older still and lands on Bots, so a site link names its tab.
 * `params` are kept, so a site switch on the GA4 view stays on it.
 */
export function insightsPath(tab: InsightsTab, siteId = 0, params?: URLSearchParams): string {
  const path = siteId > 0 ? `/insights/${tab}/${siteId}` : `/insights/${tab}`;
  const q = new URLSearchParams(params);
  if (tab === "audience") q.set("view", "ga4");
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}
